import { RotateCcw, ShieldAlert, Mail, Clock, CheckCircle2, XCircle } from "lucide-react";

export default function RefundsPage() {
  const subject = encodeURIComponent("Refund request — Teddys Tiny Friends");
  const body = encodeURIComponent(
    "Hi Teddys Tiny Friends,\n\n" +
    "I'd like to request a refund for the following order:\n\n" +
    "Order date: \nOrder name (the one used at checkout): \nEmail used: \nAnimal(s) ordered: \n\nReason for the request: \n\n" +
    "Thank you."
  );
  const mailto = `mailto:info@teddystinyfriends.co.uk?subject=${subject}&body=${body}`;

  return (
    <div className="container-x py-16 lg:py-24" data-testid="refunds-page">
      <div className="max-w-3xl">
        <div className="font-script text-2xl text-[#D97757]">refunds & returns</div>
        <h1 className="font-serif text-5xl lg:text-6xl mt-2 leading-tight">
          Our little refund promise.
        </h1>
        <p className="mt-6 text-lg text-[#5C4D43] leading-relaxed">
          We&apos;re a brand-new family-run studio and your happiness genuinely matters to us.
          Every friend is hand-crocheted to order, so we treat refunds with the same care we put
          into the stitches. Here is our policy in plain English — based on UK consumer law.
        </p>
      </div>

      {/* QUICK SUMMARY */}
      <div className="mt-12 grid lg:grid-cols-2 gap-6">
        <div className="card-soft p-6 lg:p-7" data-testid="refund-yes">
          <CheckCircle2 className="text-[#A3B18A]" />
          <div className="font-serif text-2xl mt-4">When we&apos;ll always refund</div>
          <ul className="mt-3 space-y-2 text-[#5C4D43] leading-relaxed text-sm">
            <li>• Item arrives <strong>damaged or faulty</strong></li>
            <li>• Item is <strong>not as described</strong></li>
            <li>• <strong>Wrong item</strong> sent</li>
            <li>• Order is <strong>lost in the post</strong> for 21+ working days</li>
            <li>• You changed your mind on a <strong>non-personalised</strong> stock item — within 14 days of delivery (UK Consumer Contracts Regulations 2013)</li>
          </ul>
        </div>

        <div className="card-soft p-6 lg:p-7" data-testid="refund-no">
          <XCircle className="text-[#D97757]" />
          <div className="font-serif text-2xl mt-4">When we sadly can&apos;t refund</div>
          <ul className="mt-3 space-y-2 text-[#5C4D43] leading-relaxed text-sm">
            <li>• A <strong>personalised order</strong> (custom animal, embroidered name or birthday) — unless it&apos;s faulty or wrong, these are exempt under UK law because they&apos;re made specifically for you</li>
            <li>• Normal signs of love (a bit of cuddle wear after a few weeks)</li>
            <li>• Items returned after the 14-day window without a fault</li>
            <li>• Items damaged by washing in a machine (please surface-clean only)</li>
          </ul>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <div className="mt-14">
        <h2 className="font-serif text-3xl lg:text-4xl">How a refund works</h2>
        <div className="mt-8 grid md:grid-cols-3 gap-6">
          <Step icon={Mail} n="01" title="Email us"
            text="Send a message to info@teddystinyfriends.co.uk with your order details and a photo of the issue if relevant. Or tap the button below — it pre-fills the email for you." />
          <Step icon={Clock} n="02" title="We reply within 48 hours"
            text="We'll let you know if the refund qualifies, request a return if needed (we'll send a freepost address for faulty/wrong items) and confirm what happens next." />
          <Step icon={RotateCcw} n="03" title="Refund to your card"
            text="Approved refunds are processed via Stripe back to the original card within 3-5 working days. You'll get an automatic email from Stripe when it lands." />
        </div>
      </div>

      {/* STATUTORY RIGHTS */}
      <div className="mt-14 p-8 lg:p-10 rounded-3xl bg-[#F4EFE6]" data-testid="statutory-rights">
        <div className="flex items-start gap-4">
          <ShieldAlert className="text-[#D97757] shrink-0 mt-1" />
          <div>
            <div className="font-serif text-2xl">Your statutory rights</div>
            <p className="mt-3 text-[#5C4D43] leading-relaxed">
              Nothing in this policy affects your statutory rights under the
              <strong> Consumer Rights Act 2015</strong> (right to a refund for faulty goods
              within 30 days) and the <strong>Consumer Contracts Regulations 2013</strong>
              (14-day cooling-off period for non-personalised distance sales). If you live in
              the UK, those laws always apply. We&apos;re a tiny shop and we always try to do
              the right thing.
            </p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="mt-14 grid lg:grid-cols-[2fr_1fr] gap-6 items-center p-8 lg:p-10 rounded-3xl bg-[#2C211B] text-white" data-testid="refund-cta">
        <div>
          <div className="font-script text-xl text-[#D8A4A4]">need a refund?</div>
          <div className="font-serif text-3xl mt-1">Drop us a line — we&apos;ll sort it out together.</div>
          <p className="mt-3 text-white/75 text-sm">
            One tap opens your email app pre-filled with the right details. We&apos;ll reply within 48 hours.
          </p>
        </div>
        <div className="lg:justify-self-end">
          <a
            href={mailto}
            data-testid="refund-email-btn"
            className="btn-primary inline-flex items-center gap-2"
          >
            <Mail size={16} /> Request a refund
          </a>
        </div>
      </div>
    </div>
  );
}

function Step({ icon: Icon, n, title, text }) {
  return (
    <div className="card-soft p-6" data-testid={`refund-step-${n}`}>
      <div className="flex items-center gap-3">
        <Icon className="text-[#D97757]" size={20} />
        <div className="font-serif text-2xl text-[#D97757]">{n}</div>
      </div>
      <div className="font-serif text-xl mt-3">{title}</div>
      <p className="mt-2 text-sm text-[#5C4D43] leading-relaxed">{text}</p>
    </div>
  );
}
